import { useState, useEffect } from "react";
import AdminLayout from "../layout/AdminLayout";
import { API_BASE_URL } from "../../config/api";
import { adminAuthHeader } from "../../utils/authHeader";
import PasswordInput from "../../components/PasswordInput";

const AdminProfile = () => {
  const [admin,   setAdmin]   = useState(null);
  const [loading, setLoading] = useState(true);

  // Change password state
  const [showChangePwd,  setShowChangePwd]  = useState(false);
  const [currentPwd,     setCurrentPwd]     = useState("");
  const [newPwd,         setNewPwd]         = useState("");
  const [confirmPwd,     setConfirmPwd]     = useState("");
  const [pwdLoading,     setPwdLoading]     = useState(false);
  const [pwdMsg,         setPwdMsg]         = useState("");
  const [pwdError,       setPwdError]       = useState("");

  // Fetch real admin profile
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res  = await fetch(`${API_BASE_URL}/api/auth/me`, {
          headers: adminAuthHeader(),
        });
        const data = await res.json();
        if (res.ok) setAdmin(data);
      } catch (err) {
        console.error("Failed to load admin profile");
      }
      setLoading(false);
    };
    fetchProfile();
  }, []);

  // Change password handler
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwdMsg(""); setPwdError("");

    if (newPwd !== confirmPwd) {
      setPwdError("New passwords do not match.");
      return;
    }
    if (newPwd.length < 6) {
      setPwdError("New password must be at least 6 characters.");
      return;
    }

    setPwdLoading(true);
    try {
      const res  = await fetch(`${API_BASE_URL}/api/auth/change-password`, {
        method: "PUT",
        headers: { ...adminAuthHeader(), "Content-Type": "application/json" },
        body: JSON.stringify({ currentPassword: currentPwd, newPassword: newPwd }),
      });
      const data = await res.json();

      if (res.ok) {
        setPwdMsg("✅ Password changed successfully!");
        setCurrentPwd(""); setNewPwd(""); setConfirmPwd("");
        setTimeout(() => { setShowChangePwd(false); setPwdMsg(""); }, 2000);
      } else {
        setPwdError(data.message || "Password change failed.");
      }
    } catch {
      setPwdError("Cannot connect to server.");
    }
    setPwdLoading(false);
  };

  return (
    <AdminLayout>
      <div className="admin-page-wrapper">
        <div className="admin-header">
          <p className="admin-welcome">Profile</p>
          <h2 className="admin-title">Admin Account</h2>
        </div>

        <div className="admin-card admin-profile-card">
          {loading ? (
            <p style={{ color: "#a3aed0" }}>Loading profile…</p>
          ) : (
            <>
              {/* TOP */}
              <div className="admin-profile-top">
                <div className="admin-profile-avatar">
                  <img
                    src={admin?.profileImage || "https://via.placeholder.com/80"}
                    alt="Admin"
                  />
                </div>
                <div className="admin-profile-info">
                  <h3>{admin?.name || "Admin"}</h3>
                  <p className="admin-role">Super Administrator</p>
                </div>
              </div>

              {/* DETAILS */}
              <div className="admin-profile-details">
                <div className="profile-row">
                  <span>Email</span>
                  <strong>{admin?.email || "—"}</strong>
                </div>
                <div className="profile-row">
                  <span>Phone</span>
                  <strong>{admin?.phone || "—"}</strong>
                </div>
                <div className="profile-row">
                  <span>Last Login</span>
                  <strong>
                    {admin?.lastLogin
                      ? new Date(admin.lastLogin).toLocaleString("en-IN", {
                          day: "2-digit", month: "short", year: "numeric",
                          hour: "2-digit", minute: "2-digit"
                        })
                      : "—"}
                  </strong>
                </div>
              </div>

              {/* CHANGE PASSWORD */}
              <div className="admin-profile-actions" style={{ marginTop: "20px" }}>
                {!showChangePwd ? (
                  <button
                    onClick={() => setShowChangePwd(true)}
                    style={btnStyle}
                  >
                    🔑 Change Password
                  </button>
                ) : (
                  <div style={{ maxWidth: "380px", marginTop: "8px" }}>
                    <h4 style={{ fontSize: "15px", fontWeight: "700", color: "#1b2559", marginBottom: "14px" }}>
                      Change Password
                    </h4>

                    {pwdMsg && (
                      <div style={successBox}>{pwdMsg}</div>
                    )}
                    {pwdError && (
                      <div style={errorBox}>⚠️ {pwdError}</div>
                    )}

                    <form onSubmit={handleChangePassword}>
                      <PasswordInput
                        placeholder="Current Password"
                        value={currentPwd}
                        onChange={e => setCurrentPwd(e.target.value)}
                        required
                        style={inputStyle}
                      />
                      <PasswordInput
                        placeholder="New Password"
                        value={newPwd}
                        onChange={e => setNewPwd(e.target.value)}
                        required
                        style={inputStyle}
                      />
                      <PasswordInput
                        placeholder="Confirm New Password"
                        value={confirmPwd}
                        onChange={e => setConfirmPwd(e.target.value)}
                        required
                        style={inputStyle}
                      />

                      <div style={{ display: "flex", gap: "10px", marginTop: "4px" }}>
                        <button type="submit" style={btnStyle} disabled={pwdLoading}>
                          {pwdLoading ? "Saving..." : "Save Password"}
                        </button>
                        <button
                          type="button"
                          onClick={() => { setShowChangePwd(false); setPwdError(""); setPwdMsg(""); }}
                          style={cancelStyle}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

const inputStyle = {
  width: "100%", height: "42px", padding: "0 14px",
  borderRadius: "10px", border: "1px solid #e0e5f2",
  fontSize: "14px", marginBottom: "12px", outline: "none",
  boxSizing: "border-box",
};
const btnStyle = {
  padding: "10px 20px", background: "#4318ff", color: "#fff",
  border: "none", borderRadius: "10px", fontWeight: "600",
  fontSize: "14px", cursor: "pointer",
};
const cancelStyle = {
  padding: "10px 20px", background: "#f4f7fe", color: "#1b2559",
  border: "none", borderRadius: "10px", fontWeight: "600",
  fontSize: "14px", cursor: "pointer",
};
const successBox = {
  background: "#f0fdf4", color: "#15803d", padding: "10px 14px",
  borderRadius: "8px", marginBottom: "12px", fontSize: "14px",
};
const errorBox = {
  background: "#fee2e2", color: "#dc2626", padding: "10px 14px",
  borderRadius: "8px", marginBottom: "12px", fontSize: "14px",
};

export default AdminProfile;
